import { cache, client } from "@/client/apollo";
import {
  AnswerBlockFragment,
  AnswerBlockFragmentDoc,
  BlockSnapshotFragment,
  BlockSnapshotFragmentDoc,
  SaveFragment,
  SaveFragmentDoc,
  WorkAnswerBlocksFragment,
  WorkAnswerBlocksFragmentDoc,
  WorkAttemptCountFragment,
  WorkAttemptCountFragmentDoc,
} from "@/generated/graphql";

/**
 * Deprecating in favour of answer blocks.
 * Read the latest `Save` value from the cache.
 *
 * This is only used to keep track of the save ID references.
 */
export function readLatestSave(): SaveFragment | null {
  return client.readFragment({
    id: "Save:latest_save",
    fragment: SaveFragmentDoc,
    fragmentName: "Save",
  });
}

/**
 * Read Work's next expected Submission attempt count from the cache.
 */
export function readWorkAttemptCount(workId: string): number | null {
  const data = client.readFragment<WorkAttemptCountFragment>({
    id: client.cache.identify({ __typename: "Work", id: workId }),
    fragment: WorkAttemptCountFragmentDoc,
    fragmentName: "WorkAttemptCount",
  });

  return data?.attemptCount ?? null;
}

/**
 * List all AnswerBlocks and their latest snapshots in the singleton Work.
 */
export function readAnswerBlocks(workId: string): AnswerBlockFragment[] {
  const result = client.readFragment<WorkAnswerBlocksFragment>({
    id: cache.identify({ __typename: "Work", id: workId }),
    fragment: WorkAnswerBlocksFragmentDoc,
    fragmentName: "WorkAnswerBlocks",
  });
  return result?.answerBlocks ?? [];
}

/**
 * Read the AnswerBlock with the given ID from the cache.
 */
export function readAnswerBlock(
  answerBlockId: string
): AnswerBlockFragment | null {
  return client.readFragment({
    id: `AnswerBlock:${answerBlockId}`,
    fragment: AnswerBlockFragmentDoc,
    fragmentName: "AnswerBlock",
  });
}

/**
 * Read the latest known BlockSnapshot fragment for an AnswerBlock.
 *
 * Latest BlockSnapshot objects are stored with a fixed ID in the Apollo cache
 * with the pattern `${ANSWER_BLOCK_ID}:latest_snapshot`.
 */
export function readLatestBlockSnapshot(
  answerBlockId: string
): BlockSnapshotFragment | null {
  return client.readFragment({
    id: `BlockSnapshot:{"cacheId":"${answerBlockId}:latest_snapshot"}`,
    fragment: BlockSnapshotFragmentDoc,
    fragmentName: "BlockSnapshot",
  });
}

/**
 * List all AnswerBlocks but with their latest snapshot replaced with the
 * matching snapshot from `snapshots`.
 *
 * If an AnswerBlock does not have a matching snapshot, its `latestSnapshot` is
 * set to null.
 *
 * Useful to match submitted Block Snapshots with the current list of Answer
 * Blocks loaded in cache.
 */
export function readAnswerBlocksWithSnapshots(
  workId: string,
  snapshots: BlockSnapshotFragment[]
): AnswerBlockFragment[] {
  const answerBlocks = readAnswerBlocks(workId);
  return snapshots.flatMap((snapshot) => {
    const answerBlock = answerBlocks.find(
      (blk) => blk.id === snapshot.answerBlockId
    );
    if (answerBlock) {
      return [
        {
          ...answerBlock,
          latestBlockSnapshot: snapshot,
        },
      ];
    }
    return [];
  });
}
